import { useEffect } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered } from "lucide-react";

// Strict allow-list — mirrors the server (only tags the editor can emit, zero attributes/URLs).
const SANITIZE_CFG = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li"],
  ALLOWED_ATTR: [],
  FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "img", "a"],
  FORBID_ATTR: ["style", "class", "href", "src", "srcset"],
  ALLOW_DATA_ATTR: false,
  USE_PROFILES: { html: true },
};

export function sanitizeRichHtml(html) {
  return DOMPurify.sanitize(html || "", SANITIZE_CFG);
}

export function htmlToText(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = sanitizeRichHtml(html);
  return (tmp.textContent || tmp.innerText || "").trim();
}

export function isHtmlEmpty(html) {
  return htmlToText(html).length === 0;
}

const TOOLS = [
  { cmd: "toggleBold", icon: Bold, name: "bold", label: "Bold" },
  { cmd: "toggleItalic", icon: Italic, name: "italic", label: "Italic" },
  { cmd: "toggleUnderline", icon: UnderlineIcon, name: "underline", label: "Underline" },
  { cmd: "toggleBulletList", icon: List, name: "bulletList", label: "Bulleted list" },
  { cmd: "toggleOrderedList", icon: ListOrdered, name: "orderedList", label: "Numbered list" },
];

export function RichText({ value, onChange, placeholder, testid, disabled, minHeight = 110 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, codeBlock: false, blockquote: false, horizontalRule: false }),
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    editable: !disabled,
    immediatelyRender: false,
    // Don't re-render the React tree on every transaction — that recreates the
    // EditorContent DOM node during a focus transaction and steals focus (jumps to toolbar).
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? "" : editor.getHTML()),
    editorProps: {
      attributes: { class: "richtext-editor outline-none", style: `min-height:${minHeight}px`, "data-testid": testid || "richtext" },
      // Purge disallowed markup from pasted content before TipTap parses it into its schema.
      transformPastedHTML: (html) => sanitizeRichHtml(html),
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return; // never reset while the user is editing
    const cur = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== cur) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  return (
    <div className={`elevated ${disabled ? "opacity-70" : ""}`}
      onMouseDown={(e) => {
        // Clicking the padding/empty area (outside the editable node) still focuses the editor.
        if (!disabled && !editor.isFocused && !e.target.closest(".richtext-editor") && !e.target.closest("button")) {
          e.preventDefault();
          editor.commands.focus();
        }
      }}>
      {!disabled && <Toolbar editor={editor} testid={testid} />}
      <EditorContent editor={editor} className="px-3 py-2.5 text-base leading-relaxed" />
    </div>
  );
}

// Isolated so its useEditorState re-renders (on selection change) never touch the
// sibling EditorContent DOM node — which is what caused focus to jump to the toolbar.
function Toolbar({ editor, testid }) {
  const active = useEditorState({
    editor,
    selector: ({ editor }) => (editor
      ? { bold: editor.isActive("bold"), italic: editor.isActive("italic"), underline: editor.isActive("underline"), bulletList: editor.isActive("bulletList"), orderedList: editor.isActive("orderedList") }
      : {}),
  }) || {};
  return (
    <div className="flex items-center gap-1 border-b border-white/10 px-2 py-1.5">
      {TOOLS.map((b) => (
        <button key={b.name} type="button" title={b.label}
          data-testid={`rt-${b.name}${testid ? "-" + testid : ""}`}
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus()[b.cmd]().run(); }}
          className={`p-1.5 hover:bg-white/10 transition-colors ${active[b.name] ? "bg-white/15 text-white" : "text-[var(--txt-secondary)]"}`}>
          <b.icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export function RichView({ html, className = "" }) {
  if (isHtmlEmpty(html)) return null;
  return <div className={`richtext ${className}`} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }} />;
}
